import type { Condition } from '../../scranner/types';
import type { QueryClient } from '@tanstack/query-core';
import type { ScannerPlan } from '@thinkalpha/platform-ws-client/contracts/ideas/index.js';
import { IdeaDTO } from 'src/dtos/Idea';
import type { IdeaPlanToStageOptions } from 'src/dtos/Idea';
import { SearchPlanDTO } from 'src/dtos/Idea/SearchPlanDTO';
import type { IndicatorImportModel } from 'src/dtos/IndicatorImport';
import { SCRANNER_CHANGE_EVENT, ScrannerPlanDTO } from 'src/features/scranner/dtos/ScrannerPlanDTO';
import type { ScrannerChangeEvent } from 'src/features/scranner/dtos/ScrannerPlanDTO';
import { conditionGroupModelToIfThenGroup } from 'src/features/searchAlpha/dtos/ConditionGroupModel/conditionGroupModelToIfThenGroup';
import { FilterStage, SourceStage } from 'src/lib/tableStages';
import type { Stage } from 'src/lib/tableStages';
import type { SlugMapService } from 'src/services/SlugMapService';
import { container } from 'src/StaticContainer';
import type { ReactBindings } from 'src/types/bindings';
import { v4 } from 'uuid';

const _log = container.get('Logger').getSubLogger({ name: 'feat:scanner:ScannerModelDTO' });

interface ScannerPlanDTOInitializer {
    isDirty: boolean;
    conditions: Condition[];
    mode: 'exclude' | 'include-any' | 'include-all';
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- marker interface
interface ScannerPlanDTOState {
    //
}

export class ScannerPlanDTO extends ScrannerPlanDTO {
    constructor(
        state: ScannerPlanDTOInitializer | undefined,
        queryClient: QueryClient,
        formulaService: ReactBindings['FormulaService'],
        slugMapService: SlugMapService,
    ) {
        super(state, queryClient, formulaService);

        this.#formulaService = formulaService;
        this.#slugMapService = slugMapService;

        this.#state = state ?? {};
    }

    #formulaService: ReactBindings['FormulaService'];
    #slugMapService: SlugMapService;

    override toContract(): ScannerPlan {
        return {
            ...this.toScrannerContract(),
            type: 'scanner',
        };
    }

    static async fromPlan(
        plan: ScannerPlan,
        queryClient: ReactBindings['QueryClient'],
        formulaService: ReactBindings['FormulaService'],
        slugMapService: SlugMapService,
        options?: { isDirty?: boolean },
    ): Promise<ScannerPlanDTO> {
        return new ScannerPlanDTO(
            {
                ...(await ScrannerPlanDTO.fromPlanToInitializer(plan, queryClient, formulaService)),
                isDirty: options?.isDirty ?? false,
            },
            queryClient,
            formulaService,
            slugMapService,
        );
    }

    #state: ScannerPlanDTOState;

    private set state(newState: ScannerPlanDTOState) {
        this.#state = newState;
        this.changeTarget.dispatchEvent(new CustomEvent(SCRANNER_CHANGE_EVENT) satisfies ScrannerChangeEvent);
    }

    private static makeEmptySearchPlan() {
        return SearchPlanDTO.fromPlanAndImports(
            {
                type: 'if-then',
                root: {
                    type: 'group',
                    name: 'TODO',
                    collapsed: false,
                    enabled: true,
                    lines: [
                        {
                            type: 'line',
                            enabled: true,
                            id: v4(),
                            mode: 'formula',
                            formula: 'true == false',
                            description: '',
                            fieldInView: 'formula',
                        },
                    ],
                    id: v4(),
                    description: '',
                    operator: 'and',
                },
                imports: [],
            },
            [],
        );
    }

    async toSearchPlan(): Promise<SearchPlanDTO> {
        if (!this.conditions.some((x) => x.enabled)) {
            return ScannerPlanDTO.makeEmptySearchPlan();
        }

        await this.#slugMapService.initialize();

        const imports: IndicatorImportModel[] = [];

        const conditions = await this.toConditionGroupModel(imports);
        if (!conditions) {
            return ScannerPlanDTO.makeEmptySearchPlan();
        }

        return SearchPlanDTO.fromPlanAndImports(
            {
                type: 'if-then',
                root: conditionGroupModelToIfThenGroup(conditions),
                imports: imports.values().toArray(),
            },
            imports,
        );
    }

    override async toStage(
        inputStage: Stage | null,
        _queryClient: QueryClient | any,
        { validationMode, dwellTime }: IdeaPlanToStageOptions,
    ): Promise<FilterStage | null> {
        if (!validationMode) {
            const result = await this.renderToFormula();
            if (!result) return null;

            const { formula, imports } = result;

            return FilterStage.fromInputStage(
                inputStage ?? SourceStage.allForKey('ticker'),
                {
                    type: 'filter',
                    formula,
                    imports,
                    dwellTime,
                },
                [],
            );
        } else {
            const result = await this.getValidationModeFormulaAndIndicators();
            if (!result) return null;

            const [bespokeIndicators, formula] = result;

            return FilterStage.fromInputStage(
                inputStage ?? SourceStage.allForKey('ticker'),
                {
                    type: 'filter',
                    formula: formula.formula,
                    imports: formula.imports,
                    dwellTime,
                },
                bespokeIndicators,
            );
        }
    }

    /**
     * @deprecated - This is a temporary measure for creating runnable ideas. Long-term we should keep idea states.
     */
    toTempIdea(): IdeaDTO<ScannerPlanDTO> {
        return IdeaDTO.fromIdeaAndPlan(
            {
                name: 'Scanner Idea',
                key: 'ticker',
                isTemplate: false,
                description: null,
                defaultUniverseId: null,
                defaultColumnTemplateId: null,
            },
            this,
        ) as IdeaDTO<ScannerPlanDTO>;
    }

    async renderToFormula() {
        const searchPlanDTO = await this.toSearchPlan();
        return searchPlanDTO.renderToFormula();
    }

    async getValidationModeFormulaAndIndicators() {
        const searchPlanDTO = await this.toSearchPlan();
        return searchPlanDTO.getValidationModeFormulaAndIndicators();
    }
}
